import { useState, useRef, useEffect, useId } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useUnreadCount } from '@/hooks/useUnreadCount';
import NotificationDropdown from './NotificationDropdown';

/**
 * Bell icon button with unread badge and notification dropdown.
 *
 * - 44px min hit area with a 36px visual circle inside (matches TopBar theme toggle).
 * - Red count pill at top-right when unread > 0; caps display at 99+.
 * - Zero-state distinguisher: a small muted dot at top-right + hover tooltip,
 *   so the bell is visually distinct from the adjacent theme toggle.
 * - Full ARIA: aria-label, aria-expanded, aria-haspopup, aria-controls.
 * - Focus returns to bell button when dropdown closes.
 * - Outside-click and Escape close both handled (Escape is inside NotificationDropdown).
 *
 * No animations (pulse/shake) — per "polished and minimal" UX rule.
 */
function NotificationBell() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownId = useId();
  const tooltipId = useId();

  const { data: countData } = useUnreadCount();
  const unreadCount = countData?.unread_count ?? 0;
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  // Outside-click handler — matches TopBar profile-menu pattern
  useEffect(() => {
    if (!isOpen) return;
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  function handleClose() {
    setIsOpen(false);
    // Return focus to bell button
    bellButtonRef.current?.focus();
  }

  // Don't render the bell when there's no authenticated user
  if (!user) return null;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* 44px hit area wrapping a 36px visual circle */}
      <button
        ref={bellButtonRef}
        type="button"
        aria-label="Notifications"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={dropdownId}
        aria-describedby={unreadCount === 0 ? tooltipId : undefined}
        onClick={() => setIsOpen((prev) => !prev)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        style={{
          minWidth: '44px',
          minHeight: '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        {/* 36px visual circle — same tokens as TopBar theme toggle */}
        <span
          aria-hidden="true"
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '999px',
            border: `1px solid ${isOpen || isHovered ? 'var(--color-border-strong)' : 'var(--color-border-subtle)'}`,
            backgroundColor: isOpen || isHovered
              ? 'var(--color-dark-elevated-2)'
              : 'var(--color-dark-elevated-1)',
            color: 'var(--color-text-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background-color 120ms ease, border-color 120ms ease',
            position: 'relative',
          }}
        >
          <BellIcon style={{ width: '17px', height: '17px' }} />
        </span>

        {/* Unread count pill — visible when count > 0 */}
        {unreadCount > 0 && (
          <span
            aria-label={`${unreadCount} unread notifications`}
            aria-live="polite"
            aria-atomic="true"
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              minWidth: '18px',
              height: '18px',
              borderRadius: '9px',
              backgroundColor: 'var(--color-danger)',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 700,
              lineHeight: '18px',
              textAlign: 'center',
              padding: '0 4px',
              pointerEvents: 'none',
              border: '1.5px solid var(--color-dark-base)',
            }}
          >
            {badgeLabel}
          </span>
        )}

        {/* Zero-state dot — subtle indicator that the bell is interactive and
            distinct from the adjacent theme toggle. Only shown when there are
            no unread notifications. No animation per UX rule. */}
        {unreadCount === 0 && (
          <span
            data-testid="notification-zero-dot"
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '7px',
              right: '7px',
              width: '5px',
              height: '5px',
              borderRadius: '50%',
              backgroundColor: 'color-mix(in srgb, var(--color-accent) 40%, transparent)',
              border: '1px solid var(--color-dark-base)',
              pointerEvents: 'none',
            }}
          />
        )}
      </button>

      {/* Hover tooltip for zero-state — provides label context on hover/focus.
          Hidden when the dropdown is open (redundant with open state). */}
      {unreadCount === 0 && isHovered && !isOpen && (
        <div
          id={tooltipId}
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '6px',
            padding: '4px 8px',
            borderRadius: '6px',
            backgroundColor: 'var(--color-dark-elevated-1)',
            border: '1px solid var(--color-border-subtle)',
            color: 'var(--color-text-primary)',
            fontSize: '12px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 50,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          Notifications
        </div>
      )}

      {/* Notification dropdown — rendered inside the same container for the
          shared ref-based outside-click detection in NotificationDropdown */}
      <NotificationDropdown
        isOpen={isOpen}
        onClose={handleClose}
        containerRef={containerRef}
        dropdownId={dropdownId}
      />
    </div>
  );
}

export default NotificationBell;
